import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { PushpinFilled } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { Alert, Collapse, List, Row, Tooltip } from "antd";
import styled from "styled-components";
import {
  removeFavoriteWellListId,
  setFavoriteWellListId
} from "utils/typeWellFavoriteStorage";

import { useFavoriteWellListSync } from "hooks/useFavoriteWellListSync";

import { SET_FAVORITE_WELL_LIST } from "api/userArps";

import { WellListRecord, WellListRecordCollection } from "models/UserArpsModel";

import SavedFilterDetails from "components/filter/SavedFilterDetails";

import BusyIndicator from "../../data-table/BusyIndicator";
import { ApplyFilterButton } from "./ApplyFilterButton";
import { ApplyWellListButton } from "./ApplyWellListButton";
import { CopyWellListButton } from "./CopyWellListButton";
import { FavoriteWellListButton } from "./FavoriteWellListButton";

export interface ForecastWellListRecords {
  data: WellListRecordCollection;
  isLoading: boolean;
  forecastId?: string;
}

export const ForecastWellListRecords = function ({
  data,
  isLoading,
  forecastId
}: ForecastWellListRecords) {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [favoriteChangeId, setFavoriteChangeId] = useState<string | null>(null);
  const [activeWellListId, setActiveWellListId] = useState<string | null>(null);
  const [activeFilterId, setActiveFilterId] = useState<string | null>(null);
  const [isOptimisticallyUpdating, setIsOptimisticallyUpdating] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Ref to track the current mutation promise for cancellation
  const currentMutationRef = useRef<{ cancel: () => void } | null>(null);

  // Ref to track if we've already shown an error toast
  const hasShownErrorToastRef = useRef(false);

  // Apollo mutation for setting favorite well list
  const [setFavoriteMutation] = useMutation(SET_FAVORITE_WELL_LIST, {
    onCompleted: () => {
      // Mutation succeeded - keep the optimistic update
      setIsOptimisticallyUpdating(false);
      currentMutationRef.current = null;
      // Reset error toast flag on success
      hasShownErrorToastRef.current = false;
    },
    onError: () => {
      // Only show error toast once
      if (!hasShownErrorToastRef.current) {
        toast.error("Failed to update favourite well list");
        hasShownErrorToastRef.current = true;
      }
      setIsOptimisticallyUpdating(false);
      currentMutationRef.current = null;
    }
  });

  // Sync favorite state between API and local storage
  useFavoriteWellListSync({
    forecastId,
    data,
    isOptimisticallyUpdating,
    hasUserInteracted,
    setFavoriteChangeId,
    setHasUserInteracted
  });

  // Reset error toast flag when forecast changes
  useEffect(() => {
    hasShownErrorToastRef.current = false;
  }, [forecastId]);

  const handleToggleFavorite = async (changeId: string) => {
    if (!forecastId) {
      return;
    }

    // Cancel previous mutation if it's still running
    if (currentMutationRef.current) {
      currentMutationRef.current.cancel();
      currentMutationRef.current = null;
    }

    const newFavoriteId = favoriteChangeId === changeId ? null : changeId;

    // Mark that user has interacted - prevents useEffect from syncing stale data
    setHasUserInteracted(true);

    // Mark that we're doing an optimistic update
    setIsOptimisticallyUpdating(true);

    // Optimistically update UI and local storage immediately
    setFavoriteChangeId(newFavoriteId);
    if (newFavoriteId === null) {
      removeFavoriteWellListId(forecastId);
    } else {
      setFavoriteWellListId(forecastId, newFavoriteId);
    }

    // Create a cancellable mutation promise
    let cancelled = false;
    currentMutationRef.current = {
      cancel: () => {
        cancelled = true;
      }
    };

    // Call GraphQL mutation in background
    try {
      await setFavoriteMutation({
        variables: {
          input: {
            forecastId: forecastId,
            wellListRecordId: changeId
          }
        }
      });

      // If mutation was cancelled, don't do anything
      if (cancelled) {
        return;
      }

      // onCompleted will set isOptimisticallyUpdating to false
    } catch (error) {
      // If mutation was cancelled, don't show error or revert
      if (cancelled) {
        return;
      }

      // Revert optimistic update on error
      setIsOptimisticallyUpdating(false);

      // Find current favorite from API data
      const favoriteRecord = data?.wellListRecords?.find((record) => record.isFavorite);
      const currentFavorite = favoriteRecord?.changeId || null;
      setFavoriteChangeId(currentFavorite);

      if (currentFavorite) {
        setFavoriteWellListId(forecastId, currentFavorite);
      } else {
        removeFavoriteWellListId(forecastId);
      }
    }
  };

  // Sort well list records to show favorite first
  const sortedWellListRecords = data?.wellListRecords
    ? [...data.wellListRecords].sort((a, b) => {
        const aIsFavorite = a.changeId === favoriteChangeId;
        const bIsFavorite = b.changeId === favoriteChangeId;
        if (aIsFavorite && !bIsFavorite) return -1;
        if (!aIsFavorite && bIsFavorite) return 1;
        return 0; // Keep original order for non-favorites
      })
    : [];

  function getListItem(wellListRecord: WellListRecord) {
    const changeDate = new Date(wellListRecord.date);
    const isFavorite = wellListRecord.changeId === favoriteChangeId;
    const isActive = wellListRecord.changeId === activeWellListId;
    const isFilterActive = wellListRecord.changeId === activeFilterId;

    const renderWellRecords = () => {
      return wellListRecord?.wellList?.map((record) => {
        return <StyledRow key={record}>{record}</StyledRow>;
      });
    };

    return (
      <StyledListItem key={wellListRecord.changeId} $isFavorite={isFavorite}>
        <List.Item.Meta
          title={
            <TitleWrapper>
              <span>
                {wellListRecord.changedBy +
                  " " +
                  changeDate.toLocaleDateString() +
                  " " +
                  changeDate.toLocaleTimeString() +
                  " "}
              </span>
              <ButtonWrapper>
                <FavoriteWellListButton
                  isFavorite={isFavorite}
                  onToggleFavorite={async () =>
                    await handleToggleFavorite(wellListRecord.changeId)
                  }
                  changeId={wellListRecord.changeId}
                />
              </ButtonWrapper>
              {isFavorite && (
                <Tooltip title="Pinned as favourite well list">
                  <PinnedIcon>
                    <PushpinFilled />
                  </PinnedIcon>
                </Tooltip>
              )}
            </TitleWrapper>
          }
          description={
            <div>
              <div onClick={(e) => e.stopPropagation()}>
                <div></div>
                <Collapse
                  collapsible={"header"}
                  ghost
                  expandIcon={({ isActive }) => (
                    <div className={`collapseChevron ${isActive ? "active" : ""}`}></div>
                  )}>
                  <Collapse.Panel
                    header={
                      <HeaderWrapper>
                        <HeaderText>{`View Well List (${
                          wellListRecord?.wellList?.length || "0"
                        })`}</HeaderText>
                        <ButtonWrapper>
                          <CopyWellListButton
                            wellListRecord={wellListRecord}
                            setErrorMessage={setErrorMessage}
                          />
                          <ApplyWellListButton
                            wellListRecord={wellListRecord}
                            setErrorMessage={setErrorMessage}
                            isActive={isActive}
                            onToggleActive={setActiveWellListId}
                            onClearFilter={() => setActiveFilterId(null)}
                          />
                        </ButtonWrapper>
                      </HeaderWrapper>
                    }
                    key={`wellList${wellListRecord.changeId}`}>
                    {renderWellRecords()}
                  </Collapse.Panel>
                </Collapse>
              </div>
              {wellListRecord?.filterJSON && (
                <div onClick={(e) => e.stopPropagation()}>
                  <div></div>
                  <Collapse
                    collapsible={"header"}
                    ghost
                    expandIcon={({ isActive }) => (
                      <div
                        className={`collapseChevron ${isActive ? "active" : ""}`}></div>
                    )}>
                    <Collapse.Panel
                      header={
                        <HeaderWrapper>
                          <HeaderText>{`View Filter`}</HeaderText>
                          <ButtonWrapper>
                            <ApplyFilterButton
                              filterJSON={wellListRecord.filterJSON}
                              setErrorMessage={setErrorMessage}
                              isActive={isFilterActive}
                              changeId={wellListRecord.changeId}
                              onToggleActive={setActiveFilterId}
                              onClearWellList={() => setActiveWellListId(null)}
                            />
                          </ButtonWrapper>
                        </HeaderWrapper>
                      }
                      key={`wellList${wellListRecord.changeId}`}>
                      {
                        <SavedFilterDetails
                          filter={JSON.parse(wellListRecord.filterJSON)}
                        />
                      }
                    </Collapse.Panel>
                  </Collapse>
                </div>
              )}
            </div>
          }
        />
      </StyledListItem>
    );
  }

  return (
    <>
      {<BusyIndicator visible={isLoading} />}
      {errorMessage && (
        <AlertWrapper>
          <Alert message={errorMessage} type="error" />
        </AlertWrapper>
      )}
      {!isLoading && (
        <ListWrapper>
          <List
            dataSource={sortedWellListRecords}
            renderItem={(item) => getListItem(item)}
          />
        </ListWrapper>
      )}
    </>
  );
};

const ListWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
`;

const AlertWrapper = styled.div`
  position: absolute;
  display: flex;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  justify-content: center;
  align-items: center;
  z-index: 1;
  background-color: #eeeeee76;
`;

const HeaderWrapper = styled.div`
  /* To align button with the header text */
  height: 23px;
  align-items: center;

  display: flex;
  gap: 12px;
`;

const HeaderText = styled.div``;

const ButtonWrapper = styled.div`
  /* To align button with the header text */
  padding-top: 2px;
  display: flex;
  align-items: center;
  gap: 5px;

  .ant-btn {
    color: #a2aaad;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 5px;
    width: 32px;
    height: 32px;
  }
  .ant-btn:hover {
    color: var(--color-primary);
  }
  .ant-btn.active {
    background-color: var(--color-primary);
    color: #fff;
  }
  .ant-btn.active:hover {
    background-color: #fff;
    color: var(--color-primary);
  }
`;

const StyledRow = styled(Row)`
  font-family: var(--fontMono);
`;

const StyledListItem = styled(List.Item)<{ $isFavorite: boolean }>`
  background-color: ${(props) => (props.$isFavorite ? "#e6fffb" : "transparent")};
  border-left: ${(props) =>
    props.$isFavorite ? "3px solid var(--color-primary)" : "3px solid transparent"};
  padding: 15px;
  padding-left: 12px; // accout for border
  transition: background-color 0.2s ease;

  .ant-collapse-header {
    .ant-collapse-header-text {
      flex-grow: 1 !important;
    }

    &:hover {
      .ant-collapse-expand-icon {
        .collapseChevron::after {
          border-color: var(--color-primary);
        }
      }
    }
  }

  .collapseChevron {
    transition: all 0.2s;
    transform: rotate(270deg);
    vertical-align: 0 !important;

    &::after {
      content: "";
      display: block;
      width: 7px;
      height: 7px;
      border: 1px solid var(--color-text);
      border-width: 0 1px 1px 0;
      transform: rotate(45deg);
    }

    &.active {
      transform: rotate(360deg);
    }
  }

  .ant-collapse-content-box {
    padding-left: 35px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PinnedIcon = styled.span`
  color: var(--color-primary);
  font-size: 16px;
  display: flex;
  align-items: center;
  cursor: help;

  &:hover {
    color: var(--color-primary-hover);
  }
`;
